<template>
  <ReportShell
    :title="title"
    :stats="stats"
    :selectedStats="selectedStats"
    :statsHint="statsHint"
    :printable="false"
    :exportable="false"
    statsNowrap
    @filter="reload"
    @clear="clearFilters"
    @stat-click="toggleStat"
  >
    <template slot="filters">
      <a-col :xs="24" :sm="12" :md="8" :lg="5">
        <ReportPeriodPicker v-model="period" @change="onPeriodChange" />
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="5">
        <a-select
          v-model="groupBy"
          class="full-width"
          aria-label="Agrupar por"
          @change="reload"
        >
          <a-select-option v-for="option in groupOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="5">
        <a-select
          v-model="filters.reasons"
          class="full-width"
          mode="multiple"
          allowClear
          placeholder="Evento: todos"
          :maxTagCount="1"
          @change="reload"
        >
          <a-select-option v-for="option in reasonOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="4">
        <a-input
          v-model="filters.receiverPhone"
          allowClear
          placeholder="Destinatário (telefone)"
          @pressEnter="reload"
          @change="onPhoneChange"
        />
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="4">
        <a-select
          v-model="filters.statuses"
          class="full-width"
          mode="multiple"
          allowClear
          placeholder="Status: todos"
          :maxTagCount="1"
          @change="reload"
        >
          <a-select-option v-for="option in statusOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </a-select-option>
        </a-select>
      </a-col>
    </template>

    <a-table
      v-if="groupBy === MESSAGES"
      key="messages"
      size="small"
      row-key="id"
      :columns="columns"
      :data-source="messages"
      :loading="isLoading"
      :pagination="pagination"
      :locale="{ emptyText: 'Nenhuma mensagem no período.' }"
      @change="(pagination) => loadPage(pagination.current)"
    />
    <!-- Cada agrupamento tem colunas próprias: a key remonta a tabela ao trocar. -->
    <a-table
      v-else
      :key="groupBy"
      size="small"
      :row-key="groupRowKey"
      :columns="groupColumns"
      :data-source="groups"
      :loading="isLoading"
      :pagination="pagination"
      :locale="{ emptyText: 'Nenhuma mensagem no período.' }"
      @change="onGroupTableChange"
    />
  </ReportShell>
</template>

<script>
import * as CONSTANTS from "@/constants/constants";
import moment from "moment";
import ReportPeriodPicker from "../components/ReportPeriodPicker.vue";
import ReportShell from "../components/ReportShell.vue";
import { currentMonth, periodParams } from "../components/report-period";

/** Códigos de `reason` gravados hoje, os mesmos rótulos da auditoria do shiso. */
const REASON_LABELS = {
  ORDER_INITIAL: "Novo pedido",
  REORDER_INITIAL: "Pedido (PDF)",
  ORDER_CANCEL: "Pedido cancelado",
  REQUEST_INITIAL: "Nova cotação",
  REQUEST_RESEND: "Cotação reenviada",
  REQUEST_ADVANCED: "Cotação antecipada",
  REQUEST_PRORROGATED: "Cotação prorrogada",
  INVITATION_INITIAL: "Convite",
  INVITATION_NEW_CLIENT: "Convite (vendedor já cadastrado)",
};

const STATUS_LABELS = {
  accepted: "Aceita",
  sent: "Enviada",
  delivered: "Entregue",
  read: "Lida",
  failed: "Falhou",
};

// "Agrupar por", como na auditoria do shiso: o kyojin agrupa
// (GET whatsapp-report/groups); MESSAGES é a lista sem agrupar.
const MESSAGES = "messages";

const GROUP_OPTIONS = [
  { value: MESSAGES, label: "Agrupar por: nenhum (mensagens)" },
  { value: "reason", label: "Agrupar por: evento" },
  { value: "status", label: "Agrupar por: status" },
  { value: "receiver", label: "Agrupar por: destinatário" },
  { value: "receiver_reason", label: "Agrupar por: destinatário + evento" },
];

const emptySummary = () => ({ messages: 0, charge: 0, charge_per_message: 0, by_reason: [], by_status: [] });

const emptyFilters = () => ({ reasons: [], statuses: [], receiverPhone: "" });

/** Opções na ordem dos rótulos, mais qualquer código novo que o kyojin mandar. */
const options = (labels, extra) =>
  [...Object.keys(labels), ...extra.filter((code) => code && !(code in labels))].map((value) => ({
    value,
    label: labels[value] || value,
  }));

export default {
  name: "TabWhatsapp",
  components: {
    ReportPeriodPicker,
    ReportShell,
  },
  props: {
    title: { type: String, required: true },
  },
  data() {
    return {
      MESSAGES,
      groupOptions: GROUP_OPTIONS,
      groupBy: MESSAGES,
      messages: [],
      groups: [],
      // Ordenação da tabela agrupada, no formato do spatie (`-messages`).
      groupSort: null,
      // Totais do período (e do destinatário): o kyojin não aplica evento nem
      // status neles, então os cards não zeram quando a lista é filtrada.
      summary: emptySummary(),
      filters: emptyFilters(),
      total: 0,
      page: 1,
      perPage: 20,
      isLoading: false,
      // Mês corrente, o mesmo padrão do kyojin sem from/to (o ciclo de cobrança).
      period: currentMonth(),
    };
  },
  computed: {
    reasonOptions() {
      return options(REASON_LABELS, this.summary.by_reason.map((row) => row.reason));
    },
    statusOptions() {
      return options(STATUS_LABELS, this.summary.by_status.map((row) => row.status));
    },
    // Os totais do período e um card por status (o que o kyojin não devolveu
    // é 0). Os eventos ficam no filtro e no "Agrupar por: evento".
    stats() {
      const statusCount = this.countBy(this.summary.by_status, "status");

      return [
        {
          key: "all",
          title: "MENSAGENS",
          value: this.summary.messages || 0,
          suffix: "enviadas",
          footerText: "todos os eventos do período",
          borderColor: "#1890ff",
        },
        {
          key: "charge",
          title: "VALOR A COBRAR",
          value: this.brlValue(this.summary.charge),
          precision: 2,
          prefix: "R$",
          // tarifa unitária com as casas que o kyojin mandar (R$ 0,035, não R$ 0,04)
          footerText: `${Number(this.summary.charge_per_message || 0).toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
            maximumFractionDigits: 4,
          })} por mensagem`,
          borderColor: "#52c41a",
        },
        ...this.statusOptions.map((option) => this.countCard(option.value, `STATUS: ${option.label}`, statusCount[option.value], "#722ed1")),
      ];
    },
    selectedStats() {
      return this.filters.statuses;
    },
    statsHint() {
      return this.selectedStats.length
        ? "Mostrando só os status marcados. Clique num card de novo para tirá-lo do filtro, ou em Mensagens para ver todos."
        : "Mensagens de WhatsApp enviadas a fornecedores a partir das ações da sua empresa. Clique num status para filtrar a lista.";
    },
    pagination() {
      const items = this.groupBy === MESSAGES ? ["mensagem", "mensagens"] : ["grupo", "grupos"];
      return {
        current: this.page,
        pageSize: this.perPage,
        total: this.total,
        showTotal: (total) => `${total} ${total === 1 ? items[0] : items[1]}`,
      };
    },
    columns() {
      return [
        { title: "Data", key: "created_at", customRender: (t, row) => this.getDate(row.created_at) },
        { title: "Evento", key: "reason", customRender: (t, row) => this.reasonLabel(row.reason) },
        { title: "Destinatário", key: "receiver_phone", customRender: (t, row) => this.formatPhone(row.receiver_phone) },
        { title: "Status", key: "status", customRender: (t, row) => this.statusLabel(row.status) },
        { title: "Valor", key: "charge", align: "right", customRender: (t, row) => this.brl(row.charge) },
      ];
    },
    // Destinatário + evento: os eventos de um destinatário chegam em sequência
    // e o destinatário vira um bloco (rowspan), como o cliente no shiso.
    receiverSpans() {
      const spans = this.groups.map(() => 0);
      for (let start = 0; start < this.groups.length; ) {
        let end = start;
        while (end < this.groups.length && this.groups[end].receiver_phone === this.groups[start].receiver_phone) end++;
        spans[start] = end - start;
        start = end;
      }
      return spans;
    },
    groupColumns() {
      const sortable = (key) => ({
        sorter: true,
        sortOrder: this.sortOrderOf(key),
      });
      const byReceiver = this.groupBy === "receiver" || this.groupBy === "receiver_reason";
      const block = this.groupBy === "receiver_reason";

      return [
        ...(byReceiver
          ? [{
              title: "Destinatário",
              key: "receiver_phone",
              ...sortable("receiver_phone"),
              customRender: (t, row, index) => ({
                children: this.formatPhone(row.receiver_phone),
                attrs: block ? { rowSpan: this.receiverSpans[index] } : {},
              }),
            }]
          : []),
        ...(this.groupBy === "reason" || block
          ? [{ title: "Evento", key: "reason", ...sortable("reason"), customRender: (t, row) => this.reasonLabel(row.reason) }]
          : []),
        ...(this.groupBy === "status"
          ? [{ title: "Status", key: "status", ...sortable("status"), customRender: (t, row) => this.statusLabel(row.status) }]
          : []),
        { title: "Mensagens", key: "messages", align: "right", ...sortable("messages"), customRender: (t, row) => row.messages },
        { title: "Valor", key: "charge", align: "right", ...sortable("charge"), customRender: (t, row) => this.brl(row.charge) },
        { title: "Primeiro envio", key: "first_sent_at", ...sortable("first_sent_at"), customRender: (t, row) => this.getDate(row.first_sent_at) },
        { title: "Último envio", key: "last_sent_at", ...sortable("last_sent_at"), customRender: (t, row) => this.getDate(row.last_sent_at) },
        {
          title: "Ações",
          key: "actions",
          align: "center",
          customRender: (t, row) =>
            this.$createElement(
              "a-button",
              { props: { size: "small", type: "link" }, on: { click: () => this.showGroupMessages(row) } },
              "Ver mensagens"
            ),
        },
      ];
    },
  },
  mounted() {
    this.loadPage(1);
  },
  methods: {
    loadPage(page) {
      this.isLoading = true;
      const grouped = this.groupBy !== MESSAGES;
      const { reasons, statuses, receiverPhone } = this.filters;
      const phone = String(receiverPhone || "").replace(/\D/g, "");
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        // Chaves planas: o axios 0.19 transforma objeto aninhado em JSON.
        params: {
          ...periodParams(this.period),
          ...(reasons.length ? { "filter[reason]": reasons.join(",") } : {}),
          ...(statuses.length ? { "filter[status]": statuses.join(",") } : {}),
          ...(phone ? { "filter[receiver_phone]": phone } : {}),
          ...(grouped ? { group_by: this.groupBy } : {}),
          ...(grouped && this.groupSort ? { sort: this.groupSort } : {}),
          page: page,
          per_page: this.perPage,
        },
      };
      // As duas visões devolvem os mesmos `totals` (os cards).
      const url = `${CONSTANTS.API_V3_URL}/clients/whatsapp-report${grouped ? "/groups" : ""}`;

      axios
        .get(url, config)
        .then(({ data }) => {
          this.summary = { ...emptySummary(), ...data.totals };
          this.messages = grouped ? [] : data.data;
          this.groups = grouped ? data.data : [];
          this.total = data.meta.total;
          this.page = data.meta.current_page;
        })
        .catch(() => {
          this.messages = [];
          this.groups = [];
          this.total = 0;
          this.summary = emptySummary();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    reload() {
      this.loadPage(1);
    },
    onPeriodChange() {
      this.reload();
    },
    // Limpar o campo (o X do allowClear) recarrega na hora; digitar espera o Enter ou o Filtrar.
    onPhoneChange(event) {
      if (!event.target.value) this.reload();
    },
    onGroupTableChange(pagination, filters, sorter) {
      const sort = sorter && sorter.order ? `${sorter.order === "descend" ? "-" : ""}${sorter.columnKey}` : null;
      if (sort !== this.groupSort) {
        this.groupSort = sort;
        this.loadPage(1);
        return;
      }
      this.loadPage(pagination.current);
    },
    sortOrderOf(key) {
      if (!this.groupSort || this.groupSort.replace(/^-/, "") !== key) return false;
      return this.groupSort.startsWith("-") ? "descend" : "ascend";
    },
    groupRowKey(row) {
      return `${row.receiver_phone || ""}:${row.reason || ""}:${row.status || ""}`;
    },
    // "Ver mensagens": o grupo vira filtro e a tabela volta para as mensagens.
    showGroupMessages(row) {
      const filters = { ...this.filters };
      if (row.reason) filters.reasons = [row.reason];
      if (row.status) filters.statuses = [row.status];
      if (row.receiver_phone) filters.receiverPhone = row.receiver_phone;
      this.filters = filters;
      this.groupBy = MESSAGES;
      this.groupSort = null;
      this.reload();
    },
    toggleStat(key) {
      // "Valor a cobrar" é só total; "Mensagens" volta para todos
      if (key === "charge") return;
      if (key === "all") {
        if (!this.filters.reasons.length && !this.filters.statuses.length) return;
        this.filters = { ...this.filters, reasons: [], statuses: [] };
      } else {
        this.filters = { ...this.filters, statuses: this.toggled(this.filters.statuses, key) };
      }
      this.reload();
    },
    toggled(list, value) {
      return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
    },
    clearFilters() {
      this.period = currentMonth();
      this.filters = emptyFilters();
      this.groupBy = MESSAGES;
      this.groupSort = null;
      this.reload();
    },
    countBy(rows, field) {
      return (rows || []).reduce((counts, row) => ({ ...counts, [row[field]]: row.messages }), {});
    },
    countCard(key, label, messages, borderColor) {
      const count = messages || 0;
      return {
        key,
        title: label.toUpperCase(),
        value: count,
        suffix: count === 1 ? "mensagem" : "mensagens",
        footerText: this.brl(count * (this.summary.charge_per_message || 0)),
        borderColor,
      };
    },
    reasonLabel(reason) {
      return REASON_LABELS[reason] || reason || "--";
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status || "--";
    },
    // O kyojin manda o valor com até 4 casas; arredonda o meio centavo para cima, como numa fatura.
    brl(value) {
      return this.brlValue(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    brlValue(value) {
      const fine = Math.round((value || 0) * 10000);
      return Math.floor((fine + 50) / 100) / 100;
    },
    formatPhone(phone) {
      const digits = String(phone || "").replace(/\D/g, "").replace(/^55(?=\d{10,11}$)/, "");
      if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
      if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
      return phone || "--";
    },
    getDate(date) {
      return date ? moment(String(date)).format("DD/MM/YYYY HH:mm") : "--";
    },
  },
};
</script>

<style lang="scss" scoped>
.full-width {
  width: 100%;
}
</style>
